import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  Check,
  CheckCircle,
  ExternalLink,
  FileText,
  X,
} from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge, type StatusBadgeVariant } from "@/components/shared/StatusBadge";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import {
  TableActionsMenu,
  type TableAction,
} from "@/components/shared/TableActionsMenu";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  listReceiptReviews,
  resolveReceiptReview,
  type ReceiptReviewRow,
} from "@/services/platformBankTransfersService";

const REVIEW_QUERY_KEY = ["platform", "bank-transfer-reviews"] as const;

function statusVariant(status: string): StatusBadgeVariant {
  if (status === "mismatch") return "danger";
  if (status === "needs_review") return "warning";
  return "neutral";
}

function statusLabel(status: string, t: (k: string, d: string) => string): string {
  if (status === "mismatch")
    return t("bankTransferReviews.status.mismatch", "No coincide");
  if (status === "needs_review")
    return t("bankTransferReviews.status.needsReview", "Revisar");
  return t("bankTransferReviews.status.unverified", "Sin verificar");
}

function formatAmount(minor: number, currency: string): string {
  return `${currency} ${(minor / 100).toLocaleString("es-UY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export function BankTransferReviewsPage(): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pagination = usePaginationFromUrl();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [detailRow, setDetailRow] = useState<ReceiptReviewRow | null>(null);

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: [...REVIEW_QUERY_KEY, statusFilter, pagination.page, pagination.limit],
    queryFn: () =>
      listReceiptReviews({
        page: pagination.page,
        limit: pagination.limit,
        status:
          statusFilter === "all"
            ? undefined
            : (statusFilter as "needs_review" | "mismatch" | "unverified"),
      }),
  });

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  const handleStatusChange = (next: string): void => {
    setStatusFilter(next);
    pagination.goToPage(1);
  };

  const resolveMutation = useMutation({
    mutationFn: (checkoutId: string) => resolveReceiptReview(checkoutId),
    onSuccess: () => {
      toast.success(
        t("bankTransferReviews.resolved", "Marcada como verificada."),
      );
      setDetailRow(null);
      void queryClient.invalidateQueries({ queryKey: REVIEW_QUERY_KEY });
    },
    onError: () => {
      toast.error(
        t("bankTransferReviews.resolveError", "No pudimos actualizar la revisión."),
      );
    },
  });

  const rows = useMemo(() => data?.data ?? [], [data]);

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("bankTransferReviews.title", "Transferencias por revisar")}
        /* The old copy said "El plan ya está activo" for every row. That is
           true only for ANNUAL, which activates on receipt upload; a MONTHLY
           transfer is verify-first, so its plan is NOT granted and the tenant
           stays paused until someone resolves it here. This list is filtered
           by verification status alone, never by interval, so both kinds show
           up together — and the old sentence told the operator there was no
           hurry on exactly the rows where a paying customer is locked out. */
        description={t(
          "bankTransferReviews.description",
          "Pagos por transferencia bancaria cuyo comprobante la IA no pudo verificar. Las anuales ya están activas; las mensuales quedan retenidas — el plan se activa recién cuando las marcás como verificadas.",
        )}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Select value={statusFilter} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">
              {t("bankTransferReviews.filterAll", "Todas")}
            </SelectItem>
            <SelectItem value="mismatch">
              {t("bankTransferReviews.status.mismatch", "No coincide")}
            </SelectItem>
            <SelectItem value="needs_review">
              {t("bankTransferReviews.status.needsReview", "Revisar")}
            </SelectItem>
            <SelectItem value="unverified">
              {t("bankTransferReviews.status.unverified", "Sin verificar")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <TableCard>
        {isLoading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title={t("bankTransferReviews.emptyTitle", "Nada por revisar")}
            message={t(
              "bankTransferReviews.emptyMessage",
              "Todas las transferencias bancarias están verificadas.",
            )}
          />
        ) : (
          <>
            <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("bankTransferReviews.col.company", "Empresa")}</TableHead>
                <TableHead>{t("bankTransferReviews.col.plan", "Plan")}</TableHead>
                <TableHead>{t("bankTransferReviews.col.amount", "Monto")}</TableHead>
                <TableHead>{t("bankTransferReviews.col.confidence", "Confianza")}</TableHead>
                <TableHead>{t("bankTransferReviews.col.status", "Estado")}</TableHead>
                <TableHead className="w-[60px]" aria-hidden />
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const actions: TableAction[] = [
                  {
                    label: t("bankTransferReviews.viewDetail", "Ver detalle"),
                    icon: FileText,
                    onClick: () => setDetailRow(row),
                  },
                  ...(row.receiptUrl
                    ? [
                        {
                          label: t("bankTransferReviews.openReceipt", "Abrir comprobante"),
                          icon: ExternalLink,
                          onClick: () =>
                            window.open(row.receiptUrl!, "_blank", "noopener"),
                        },
                      ]
                    : []),
                  {
                    label: t("bankTransferReviews.markVerified", "Marcar verificada"),
                    icon: CheckCircle,
                    onClick: () => resolveMutation.mutate(row.checkoutId),
                  },
                ];
                return (
                  <TableRow
                    key={row.checkoutId}
                    className="cursor-pointer transition-colors duration-150 hover:bg-muted/50"
                    onClick={() => setDetailRow(row)}
                  >
                    <TableCell className="font-medium">
                      <span className="block">{row.companyName}</span>
                      {row.rut && (
                        <span className="block text-xs text-muted-foreground">
                          RUT {row.rut}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="block">{row.planLabel}</span>
                      {/* Says which rows are holding a customer out. Monthly
                          is verify-first: paid, but not granted until this
                          row is resolved. */}
                      {row.billingInterval === "monthly" && (
                        <StatusBadge variant="warning" className="mt-1">
                          {t(
                            "bankTransferReviews.heldUntilVerified",
                            "Plan retenido hasta verificar",
                          )}
                        </StatusBadge>
                      )}
                    </TableCell>
                    <TableCell>{formatAmount(row.amountMinor, row.currency)}</TableCell>
                    <TableCell>
                      {row.confidence != null
                        ? `${Math.round(row.confidence * 100)}%`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <StatusBadge variant={statusVariant(row.verificationStatus)}>
                        {statusLabel(row.verificationStatus, t)}
                      </StatusBadge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <TableActionsMenu actions={actions} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            </Table>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t("bankTransferReviews.rowsPerPage", "Filas por página")}
              pageOfLabel={t("bankTransferReviews.pageOf", {
                defaultValue: "{{from}}–{{to}} de {{total}}",
                from: (pagination.page - 1) * pagination.limit + 1,
                to: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total,
              })}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </>
        )}
      </TableCard>

      <ReceiptDetailDialog
        row={detailRow}
        onClose={() => setDetailRow(null)}
        onResolve={(id) => resolveMutation.mutate(id)}
        resolving={resolveMutation.isPending}
      />
    </div>
  );
}

function MatchRow({
  label,
  read,
  expected,
  matched,
}: {
  label: string;
  read: string;
  expected: string;
  matched: boolean | undefined;
}): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-3 border-b py-2 last:border-b-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="flex flex-col items-end text-right">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          {matched === true ? (
            <Check className="h-3.5 w-3.5 text-emerald-600" aria-hidden />
          ) : matched === false ? (
            <X className="h-3.5 w-3.5 text-red-600" aria-hidden />
          ) : null}
          {read || "—"}
        </span>
        <span className="text-xs text-muted-foreground">
          esperado: {expected}
        </span>
      </div>
    </div>
  );
}

function ReceiptDetailDialog({
  row,
  onClose,
  onResolve,
  resolving,
}: {
  row: ReceiptReviewRow | null;
  onClose: () => void;
  onResolve: (checkoutId: string) => void;
  resolving: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog open={row != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        {row ? (
          <>
            <DialogHeader>
              <DialogTitle>{row.companyName}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <StatusBadge variant={statusVariant(row.verificationStatus)}>
                  {statusLabel(row.verificationStatus, t)}
                </StatusBadge>
                <span className="text-sm text-muted-foreground">
                  {row.confidence != null
                    ? `${t("bankTransferReviews.col.confidence", "Confianza")} ${Math.round(
                        row.confidence * 100,
                      )}%`
                    : null}
                </span>
              </div>

              <div className="rounded-lg border px-3">
                <MatchRow
                  label={t("bankTransferReviews.detail.beneficiary", "Destinatario")}
                  read={row.read.beneficiary ?? "—"}
                  expected="SALKANTAI SAS"
                  matched={row.matches?.name}
                />
                <MatchRow
                  label={t("bankTransferReviews.detail.account", "Cuenta")}
                  read={row.read.account ?? "—"}
                  expected="2641884"
                  matched={row.matches?.account}
                />
                <MatchRow
                  label={t("bankTransferReviews.detail.amount", "Monto")}
                  read={
                    row.read.amount != null
                      ? `${row.read.currency ?? ""} ${row.read.amount.toLocaleString("es-UY")}`.trim()
                      : "—"
                  }
                  expected={formatAmount(row.amountMinor, row.currency)}
                  matched={row.matches?.amount}
                />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                {row.receiptUrl ? (
                  <a
                    href={row.receiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#19B5A6] hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" aria-hidden />
                    {t("bankTransferReviews.openReceipt", "Abrir comprobante")}
                  </a>
                ) : (
                  <span />
                )}
                <Button
                  onClick={() => onResolve(row.checkoutId)}
                  disabled={resolving}
                >
                  <CheckCircle className="h-4 w-4" aria-hidden />
                  {t("bankTransferReviews.markVerified", "Marcar verificada")}
                </Button>
              </div>
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
